import React, { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

const STALL_TIMEOUT_MS = 12000;
const MAX_RETRY_DELAY_MS = 10000;

export default function HlsPlayer({ channel, src }) {
  const videoRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const [reloadToken, setReloadToken] = useState(0);
  const [playerMessage, setPlayerMessage] = useState("Loading master preview...");
  const streamUrl = src || `/hls/${channel}.m3u8`;

  useEffect(() => {
    retryCountRef.current = 0;
    setReloadToken((value) => value + 1);
  }, [streamUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;

    let disposed = false;
    let hls = null;
    let progressTimer = null;
    let retryScheduled = false;
    let lastProgressAt = Date.now();
    let lastPlaybackAt = Date.now();
    let lastCurrentTime = -1;
    const separator = streamUrl.includes("?") ? "&" : "?";
    const sourceWithCacheBust = `${streamUrl}${separator}_=${Date.now()}`;

    const markProgress = () => {
      lastProgressAt = Date.now();
      retryCountRef.current = 0;
      setPlayerMessage("Master preview live");
    };

    const scheduleRetry = (reason) => {
      if (disposed || retryScheduled) return;
      retryScheduled = true;
      const attempt = retryCountRef.current + 1;
      retryCountRef.current = attempt;
      const delay = Math.min(1000 * (2 ** Math.min(attempt - 1, 3)), MAX_RETRY_DELAY_MS);
      setPlayerMessage(`${reason}. Retrying master preview...`);
      if (hls) {
        hls.destroy();
        hls = null;
      }
      retryTimerRef.current = window.setTimeout(() => {
        if (!disposed) setReloadToken((value) => value + 1);
      }, delay);
    };

    const handleVideoProgress = () => {
      if (video.currentTime > lastCurrentTime + 0.05) {
        lastCurrentTime = video.currentTime;
        lastPlaybackAt = Date.now();
        markProgress();
      }
    };

    video.addEventListener("timeupdate", handleVideoProgress);
    video.addEventListener("playing", markProgress);

    if (Hls.isSupported()) {
      hls = new Hls({
        // The masters publish two-second segments.  Keep enough live reserve
        // for a delayed manifest/fragment request instead of exhausting the
        // old eight-second buffer and visibly freezing the dashboard card.
        enableWorker: true,
        lowLatencyMode: false,
        liveSyncDurationCount: 4,
        liveMaxLatencyDurationCount: 10,
        backBufferLength: 15,
        maxBufferLength: 20,
        maxMaxBufferLength: 30,
        maxBufferHole: 0.5,
        manifestLoadingMaxRetry: 5,
        levelLoadingMaxRetry: 5,
        fragLoadingMaxRetry: 6,
        manifestLoadingRetryDelay: 500,
        levelLoadingRetryDelay: 500,
        fragLoadingRetryDelay: 500,
      });
      hls.attachMedia(video);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls?.loadSource(sourceWithCacheBust));
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        markProgress();
        video.play().catch(() => {});
      });
      hls.on(Hls.Events.FRAG_LOADED, markProgress);
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data?.fatal) return;
        scheduleRetry(data.type === Hls.ErrorTypes.MEDIA_ERROR ? "Media stalled" : "Stream unavailable");
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = sourceWithCacheBust;
      video.load();
      video.play().catch(() => {});
      video.addEventListener("loadeddata", markProgress);
      video.addEventListener("error", () => scheduleRetry("Stream unavailable"), { once: true });
    } else {
      setPlayerMessage("HLS playback is not supported in this browser");
    }

    progressTimer = window.setInterval(() => {
      handleVideoProgress();
      const noMediaProgress = Date.now() - lastPlaybackAt > STALL_TIMEOUT_MS;
      const noPlaylistProgress = Date.now() - lastProgressAt > STALL_TIMEOUT_MS;
      if (!video.paused && (noMediaProgress || noPlaylistProgress)) {
        scheduleRetry("Preview stopped advancing");
      }
    }, 3000);

    return () => {
      disposed = true;
      video.removeEventListener("timeupdate", handleVideoProgress);
      video.removeEventListener("playing", markProgress);
      if (progressTimer) window.clearInterval(progressTimer);
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
      if (hls) hls.destroy();
    };
  }, [streamUrl, reloadToken]);

  const handleRetry = () => {
    retryCountRef.current = 0;
    setPlayerMessage("Loading master preview...");
    setReloadToken((value) => value + 1);
  };

  return (
    <div className="mb-4">
      <video
        ref={videoRef}
        width="500"
        height="280"
        poster="/thumbs/colour.png"
        style={{ maxWidth: "100%", backgroundColor: "black" }}
        controls
        autoPlay
        muted
        playsInline
      />
      <div className="flex items-center justify-between gap-2 mt-1">
        <div className="text-xs text-gray-400 italic">{playerMessage}</div>
        {playerMessage !== "Master preview live" && (
          <button
            type="button"
            onClick={handleRetry}
            className="px-2 py-1 bg-blue-600 text-white text-xs rounded"
          >
            Retry
          </button>
        )}
      </div>
    </div>
  );
}
